import { useCallback, useEffect, useState, Fragment } from "react";
import {
  ActionIcon,
  Button,
  Card,
  Checkbox,
  Group,
  Loader,
  Modal,
  NumberInput,
  Paper,
  Select,
  Stack,
  Table,
  Text,
  Textarea,
  TextInput,
} from "@mantine/core";
import { modals } from "@mantine/modals";
import { notifications } from "@mantine/notifications";
import {
  IconArrowDown,
  IconArrowUp,
  IconEdit,
  IconPlus,
  IconTrash,
} from "@tabler/icons-react";
import useTransientStore from "../../../data/transcientStore";
import { useTranslation } from "../../../hooks/useTranslation";
import { usePermissions } from "../../../hooks/usePermissions";
import {
  GeneralPriceListResponse,
  GeneralPriceFormMetadata,
  GeneralPriceGroup,
  GeneralPriceItem,
  UpsertGeneralPriceGroupRequest,
  UpsertGeneralPriceItemRequest,
  createGeneralPriceGroup,
  createGeneralPriceItem,
  deleteGeneralPriceGroup,
  deleteGeneralPriceItem,
  getGeneralPriceList,
  getGeneralPriceFormMetadata,
  moveGeneralPriceGroup,
  moveGeneralPriceItem,
  updateGeneralPriceGroup,
  updateGeneralPriceItem,
} from "../../../services/GeneralPriceListService";

interface GeneralPriceListPageProps {
  onBack?: () => void;
  initialData?: GeneralPriceListResponse | null;
  onDataLoaded?: (data: GeneralPriceListResponse) => void;
}

type GroupFormState = UpsertGeneralPriceGroupRequest;
type ItemFormState = UpsertGeneralPriceItemRequest;

const emptyGroupForm: GroupFormState = {
  name: "",
  description: "",
  orderInGroup: 0,
  includeInReport: false,
  isActive: true,
  objectId: null,
  currentOrderInGroup: null,
};

const emptyItemForm: ItemFormState = {
  priceGroupId: 0,
  name: "",
  description: "",
  orderInGroup: 0,
  itemNumber: 0,
  itemSubnumber: 0,
  itemCode: "",
  unitName: "",
  includeInReport: false,
  requireAdditionalInfo: false,
  referenceCodes: "",
  invoicingReferenceCode: "",
  currentOrderInGroup: null,
};

const defaultUnitOptions = [
  "bd",
  "jm",
  "m",
  "m/bd",
  "m,bd",
  "m²",
  "m³",
  "m²/cm",
  "m2tr",
  "mtr",
  "m3ktr",
  "m3rtr",
  "m3itd",
  "h",
  "tim",
  "kpl",
  "kpl/t",
  "krm",
  "st",
  "t",
  "ton",
  "tn",
  "kg",
  "%",
  "Muu",
  "erä",
  "ARV",
  "Päivä",
  "vrk",
];

export default function GeneralPriceListPage({
  onBack: _onBack,
  initialData,
  onDataLoaded,
}: Readonly<GeneralPriceListPageProps>) {
  const selectedAddressSystemId =
    useTransientStore((state) => state.selectedAddressSystem?.id) ?? 0;
  const { getTranslation } = useTranslation({
    pageId: 522,
    addressSystemId: selectedAddressSystemId,
    autoLoad: true,
  });
  const { checkAccess } = usePermissions();

  const [data, setData] = useState<GeneralPriceListResponse | null>(
    initialData ?? null,
  );
  const [loading, setLoading] = useState(!initialData);
  const [movingKey, setMovingKey] = useState<string | null>(null);
  const [collapsedGroupIds, setCollapsedGroupIds] = useState<Set<number>>(
    () => new Set(),
  );
  const [groupModalOpened, setGroupModalOpened] = useState(false);
  const [itemModalOpened, setItemModalOpened] = useState(false);
  const [editingGroupId, setEditingGroupId] = useState<number | null>(null);
  const [editingItemId, setEditingItemId] = useState<number | null>(null);
  const [groupForm, setGroupForm] = useState<GroupFormState>(emptyGroupForm);
  const [itemForm, setItemForm] = useState<ItemFormState>(emptyItemForm);
  const [metadata, setMetadata] = useState<GeneralPriceFormMetadata | null>(
    null,
  );
  const [metadataLoading, setMetadataLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const canManage = checkAccess("ADMIN_PRICES");

  const loadList = useCallback(async () => {
    if (selectedAddressSystemId <= 0) {
      const emptyData = {
        hideWorkItemNumber: false,
        useWorkItemCode: false,
        priceGroupsByObject: false,
        groups: [],
      };
      setData(emptyData);
      onDataLoaded?.(emptyData);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const result = await getGeneralPriceList(selectedAddressSystemId);
      setData(result);
      onDataLoaded?.(result);
    } catch (e) {
      console.error(e);
      notifications.show({
        title: getTranslation("REPORTS_ERROR") || "Error",
        message:
          e instanceof Error
            ? e.message
            : getTranslation("MESSAGE_LOAD_LIST_FAILED") ||
              "Failed to load unit price list lines",
        color: "red",
      });
    } finally {
      setLoading(false);
    }
  }, [selectedAddressSystemId, getTranslation, onDataLoaded]);

  useEffect(() => {
    if (initialData) {
      setData(initialData);
      setLoading(false);
      return;
    }

    void loadList();
  }, [initialData, loadList]);

  const reorderRows = <T extends { id: number; orderInGroup: number }>(
    rows: T[],
    id: number,
    direction: -1 | 1,
  ): T[] | null => {
    const index = rows.findIndex((row) => row.id === id);
    const targetIndex = index + direction;
    if (index < 0 || targetIndex < 0 || targetIndex >= rows.length) {
      return null;
    }

    const nextRows = [...rows];
    [nextRows[index], nextRows[targetIndex]] = [
      nextRows[targetIndex],
      nextRows[index],
    ];
    return nextRows.map((row, rowIndex) => ({
      ...row,
      orderInGroup: rowIndex + 1,
    }));
  };

  const moveGroup = async (groupId: number, direction: -1 | 1) => {
    if (!data || movingKey) return;

    const nextGroups = reorderRows(data.groups, groupId, direction);
    if (!nextGroups) return;

    const key = `group-${groupId}-${direction}`;
    const previousData = data;
    const nextData = { ...data, groups: nextGroups };
    setData(nextData);
    onDataLoaded?.(nextData);
    setMovingKey(key);
    try {
      await moveGeneralPriceGroup(selectedAddressSystemId, groupId, direction);
    } catch (e) {
      console.error(e);
      setData(previousData);
      onDataLoaded?.(previousData);
      notifications.show({
        title: getTranslation("REPORTS_ERROR") || "Error",
        message:
          e instanceof Error
            ? e.message
            : getTranslation("MESSAGE_MOVE_GROUP_FAILED") ||
              "Failed to move group",
        color: "red",
      });
    } finally {
      setMovingKey(null);
    }
  };

  const moveItem = async (
    groupId: number,
    itemId: number,
    direction: -1 | 1,
  ) => {
    if (!data || movingKey) return;

    const group = data.groups.find(
      (currentGroup) => currentGroup.id === groupId,
    );
    if (!group) return;

    const nextItems = reorderRows(group.items, itemId, direction);
    if (!nextItems) return;

    const key = `item-${itemId}-${direction}`;
    const previousData = data;
    const nextData = {
      ...data,
      groups: data.groups.map((currentGroup) =>
        currentGroup.id === groupId
          ? { ...currentGroup, items: nextItems }
          : currentGroup,
      ),
    };
    setData(nextData);
    onDataLoaded?.(nextData);
    setMovingKey(key);
    try {
      await moveGeneralPriceItem(
        selectedAddressSystemId,
        groupId,
        itemId,
        direction,
      );
    } catch (e) {
      console.error(e);
      setData(previousData);
      onDataLoaded?.(previousData);
      notifications.show({
        title: getTranslation("REPORTS_ERROR") || "Error",
        message:
          e instanceof Error
            ? e.message
            : getTranslation("MESSAGE_MOVE_ITEM_FAILED") ||
              "Failed to move item",
        color: "red",
      });
    } finally {
      setMovingKey(null);
    }
  };

  const showError = (fallback: string, error: unknown) => {
    console.error(error);
    const message =
      (error as { response?: { data?: { message?: string } } })?.response?.data
        ?.message || (error instanceof Error ? error.message : fallback);
    notifications.show({
      title: getTranslation("REPORTS_ERROR") || "Error",
      message,
      color: "red",
    });
  };

  const loadMetadata = async (groupId?: number) => {
    setMetadataLoading(true);
    try {
      const meta = await getGeneralPriceFormMetadata(
        selectedAddressSystemId,
        groupId,
      );
      setMetadata(meta);
      return meta;
    } finally {
      setMetadataLoading(false);
    }
  };

  const getListMetadata = (): GeneralPriceFormMetadata => ({
    groupOrderOptions: [
      { value: 0, label: getTranslation("LABEL_FIRST") || "First" },
      ...(data?.groups.map((group) => ({
        value: group.orderInGroup,
        label: group.name,
      })) ?? []),
    ],
    groupOptions:
      data?.groups.map((group) => ({
        id: group.id,
        name: group.name,
        orderInGroup: group.orderInGroup,
      })) ?? [],
    objectOptions: metadata?.objectOptions ?? [],
    unitOptions: metadata?.unitOptions.length
      ? metadata.unitOptions
      : defaultUnitOptions,
  });

  const getItemMetadata = (
    group: GeneralPriceGroup,
  ): GeneralPriceFormMetadata => ({
    ...getListMetadata(),
    groupOrderOptions: [
      { value: 0, label: getTranslation("LABEL_FIRST") || "First" },
      ...group.items.map((item) => ({
        value: item.orderInGroup,
        label: item.name,
      })),
    ],
  });

  const openCreateGroup = () => {
    setEditingGroupId(null);
    setGroupForm(emptyGroupForm);
    setMetadata(getListMetadata());
    setGroupModalOpened(true);
    void loadMetadata().catch((e) => showError(
      getTranslation("MESSAGE_LOAD_FORM_DATA_FAILED") ||
        "Failed to load form data",
      e,
    ));
  };

  const openEditGroup = (group: GeneralPriceGroup) => {
    setEditingGroupId(group.id);
    setGroupForm({
      name: group.name,
      description: group.description,
      orderInGroup: group.orderInGroup,
      includeInReport: group.includeInReport,
      isActive: group.isActive,
      objectId: group.objectId ?? null,
      currentOrderInGroup: group.orderInGroup,
    });
    setMetadata(getListMetadata());
    setGroupModalOpened(true);
    void loadMetadata().catch((e) => showError(
      getTranslation("MESSAGE_LOAD_FORM_DATA_FAILED") ||
        "Failed to load form data",
      e,
    ));
  };

  const openCreateItem = (group: GeneralPriceGroup) => {
    setEditingItemId(null);
    setItemForm({
      ...emptyItemForm,
      priceGroupId: group.id,
    });
    setMetadata(getItemMetadata(group));
    setItemModalOpened(true);
    void loadMetadata(group.id).catch((e) =>
      showError(
      getTranslation("MESSAGE_LOAD_FORM_DATA_FAILED") ||
        "Failed to load form data",
      e,
    ),
    );
  };

  const openEditItem = (group: GeneralPriceGroup, item: GeneralPriceItem) => {
    setEditingItemId(item.id);
    setItemForm({
      priceGroupId: group.id,
      name: item.name,
      description: item.description,
      orderInGroup: item.orderInGroup,
      itemNumber: item.itemNumber,
      itemSubnumber: item.itemSubnumber,
      itemCode: item.itemCode ?? "",
      unitName: item.unitName,
      includeInReport: item.includeInReport,
      requireAdditionalInfo: item.requireAdditionalInfo,
      referenceCodes: item.referenceCodes,
      invoicingReferenceCode: item.invoicingReferenceCode,
      currentOrderInGroup: item.orderInGroup,
    });
    setMetadata(getItemMetadata(group));
    setItemModalOpened(true);
    void loadMetadata(group.id).catch((e) =>
      showError(
      getTranslation("MESSAGE_LOAD_FORM_DATA_FAILED") ||
        "Failed to load form data",
      e,
    ),
    );
  };

  const handleItemGroupChange = async (value: string | null) => {
    const groupId = Number(value ?? 0);
    setItemForm((current) => ({
      ...current,
      priceGroupId: groupId,
      orderInGroup: 0,
    }));
    if (groupId > 0) {
      try {
        await loadMetadata(groupId);
      } catch (e) {
        showError(
          getTranslation("MESSAGE_LOAD_GROUP_ORDER_FAILED") ||
            "Failed to load group order options",
          e,
        );
      }
    }
  };

  const saveGroup = async () => {
    setSaving(true);
    try {
      if (editingGroupId == null) {
        await createGeneralPriceGroup(selectedAddressSystemId, groupForm);
      } else {
        await updateGeneralPriceGroup(
          selectedAddressSystemId,
          editingGroupId,
          groupForm,
        );
      }
      setGroupModalOpened(false);
      await loadList();
    } catch (e) {
      showError(
        getTranslation("MESSAGE_SAVE_GROUP_FAILED") ||
          "Failed to save group",
        e,
      );
    } finally {
      setSaving(false);
    }
  };

  const saveItem = async () => {
    setSaving(true);
    try {
      if (editingItemId == null) {
        await createGeneralPriceItem(selectedAddressSystemId, itemForm);
      } else {
        await updateGeneralPriceItem(
          selectedAddressSystemId,
          editingItemId,
          itemForm,
        );
      }
      setItemModalOpened(false);
      await loadList();
    } catch (e) {
      showError(
        getTranslation("MESSAGE_SAVE_ITEM_FAILED") || "Failed to save item",
        e,
      );
    } finally {
      setSaving(false);
    }
  };

  const confirmDeleteGroup = (group: GeneralPriceGroup) => {
    modals.openConfirmModal({
      title: getTranslation("TITLE_DELETE_GROUP") || "Delete group",
      children: (
        <Text size="sm">
          {(getTranslation("PROMPT_DELETE_CONFIRM") || 'Delete "{name}"?').replace(
            "{name}",
            group.name,
          )}
        </Text>
      ),
      labels: {
        confirm: getTranslation("BUTTON_DELETE") || "Delete",
        cancel: getTranslation("BUTTON_CANCEL") || "Cancel",
      },
      confirmProps: { color: "red" },
      onConfirm: () => {
        void (async () => {
          try {
            await deleteGeneralPriceGroup(selectedAddressSystemId, group.id);
            await loadList();
          } catch (e) {
            showError(
              getTranslation("MESSAGE_DELETE_GROUP_FAILED") ||
                "Failed to delete group",
              e,
            );
          }
        })();
      },
    });
  };

  const confirmDeleteItem = (item: GeneralPriceItem) => {
    modals.openConfirmModal({
      title: getTranslation("TITLE_DELETE_ITEM") || "Delete item",
      children: (
        <Text size="sm">
          {(getTranslation("PROMPT_DELETE_CONFIRM") || 'Delete "{name}"?').replace(
            "{name}",
            item.name,
          )}
        </Text>
      ),
      labels: {
        confirm: getTranslation("BUTTON_DELETE") || "Delete",
        cancel: getTranslation("BUTTON_CANCEL") || "Cancel",
      },
      confirmProps: { color: "red" },
      onConfirm: () => {
        void (async () => {
          try {
            await deleteGeneralPriceItem(selectedAddressSystemId, item.id);
            await loadList();
          } catch (e) {
            showError(
              getTranslation("MESSAGE_DELETE_ITEM_FAILED") ||
                "Failed to delete item",
              e,
            );
          }
        })();
      },
    });
  };

  const displayNumber = (
    itemNumber: number,
    itemSubnumber: number,
    itemCode?: string | null,
  ) => {
    if (data?.useWorkItemCode && itemCode) return itemCode;
    return `${itemNumber}.${itemSubnumber}`;
  };

  const toggleGroupCollapsed = (groupId: number) => {
    setCollapsedGroupIds((current) => {
      const next = new Set(current);
      if (next.has(groupId)) {
        next.delete(groupId);
      } else {
        next.add(groupId);
      }
      return next;
    });
  };

  const collapseAllGroups = () => {
    setCollapsedGroupIds(new Set(data?.groups.map((group) => group.id) ?? []));
  };

  const expandAllGroups = () => {
    setCollapsedGroupIds(new Set());
  };

  const actionColumnStyle = {
    position: "sticky" as const,
    right: 0,
    zIndex: 1,
    minWidth: 190,
    width: 190,
    boxShadow: "-8px 0 10px -10px rgba(0, 0, 0, 0.35)",
  };
  const headerCellStyle = {
    position: "sticky" as const,
    top: 0,
    zIndex: 2,
    background: "var(--mantine-color-gray-3)",
    color: "var(--mantine-color-dark-8)",
    fontWeight: 700,
    textTransform: "uppercase" as const,
    letterSpacing: "0.03em",
  };
  const groupRowBackground = "var(--mantine-color-gray-1)";
  const inactiveGroupRowBackground = "var(--mantine-color-red-1)";

  return (
    <Card withBorder radius="lg" px="md" py="md" shadow="sm" h="100%">
      <Stack gap="md" h="100%">
        {!canManage && (
          <Text c="red" size="sm">
            {getTranslation("SETTINGS_NO_LINKS_FOR_ROLE") ||
              "No settings links match your permissions."}
          </Text>
        )}

        {loading ? (
          <Group justify="center" p="md">
            <Loader size="sm" />
          </Group>
        ) : (
          <>
            <Group justify="space-between" gap="xs">
              <Button
                size="xs"
                leftSection={<IconPlus size={14} />}
                onClick={openCreateGroup}
                disabled={!canManage}
              >
                {getTranslation("BUTTON_ADD_GROUP") || "Add group"}
              </Button>
              <Group gap="xs">
                <Button size="xs" variant="light" onClick={expandAllGroups}>
                  {getTranslation("SETTINGS_EXPAND_ALL") || "Expand all"}
                </Button>
                <Button size="xs" variant="light" onClick={collapseAllGroups}>
                  {getTranslation("SETTINGS_COLLAPSE_ALL") || "Collapse all"}
                </Button>
              </Group>
            </Group>
            <Paper
              withBorder
              radius="md"
              style={{ flex: 1, minHeight: 0, overflow: "auto" }}
            >
              <Table
                striped
                highlightOnHover
                withColumnBorders
                horizontalSpacing="sm"
                verticalSpacing="xs"
                style={{ tableLayout: "fixed", minWidth: 980 }}
              >
                <Table.Thead
                  style={{
                    position: "sticky",
                    top: 0,
                    zIndex: 4,
                  }}
                >
                  <Table.Tr bg="var(--mantine-color-gray-3)">
                    {!data?.hideWorkItemNumber && (
                      <Table.Th style={{ ...headerCellStyle, width: 110 }}>
                        {getTranslation("TEXT_LABEL_TVNRO") || "Work number"}
                      </Table.Th>
                    )}
                    <Table.Th style={{ ...headerCellStyle, minWidth: 620 }}>
                      {getTranslation("TEXT_LABEL_LAJI") || "Type"}
                    </Table.Th>
                    <Table.Th style={{ ...headerCellStyle, width: 110 }}>
                      {getTranslation("TEXT_LABEL_YKSIKKO") || "Unit"}
                    </Table.Th>
                    <Table.Th
                      ta="right"
                      bg="var(--mantine-color-gray-3)"
                      style={{
                        ...headerCellStyle,
                        ...actionColumnStyle,
                        zIndex: 3,
                      }}
                    >
                      {getTranslation("TEXT_LABEL_TOIMINNOT") || "Actions"}
                    </Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {data?.groups.length === 0 ? (
                    <Table.Tr>
                      <Table.Td colSpan={data?.hideWorkItemNumber ? 3 : 4}>
                        <Text c="dimmed" ta="center" py="xl" size="sm">
                          {getTranslation("LABEL_NO_UNIT_PRICES_AVAILABLE") ||
                            "No unit prices available"}
                        </Text>
                      </Table.Td>
                    </Table.Tr>
                  ) : (
                    data?.groups.map((group, groupIndex) => {
                      const isCollapsed = collapsedGroupIds.has(group.id);
                      return (
                        <Fragment key={group.id}>
                          <Table.Tr
                            bg={
                              group.isActive
                                ? groupRowBackground
                                : inactiveGroupRowBackground
                            }
                          >
                            {!data.hideWorkItemNumber && <Table.Td />}
                            <Table.Td
                              colSpan={2}
                              onClick={() => toggleGroupCollapsed(group.id)}
                              style={{ cursor: "pointer" }}
                            >
                              <Group gap="xs" wrap="nowrap">
                                <Text fw={700} c="dark.8">
                                  {group.name}
                                </Text>
                                <Text size="xs" c="dimmed">
                                  ({group.items.length})
                                </Text>
                              </Group>
                            </Table.Td>
                            <Table.Td
                              ta="right"
                              bg={
                                group.isActive
                                  ? groupRowBackground
                                  : inactiveGroupRowBackground
                              }
                              style={actionColumnStyle}
                            >
                              <Group
                                justify="flex-end"
                                gap={4}
                                onClick={(event) => event.stopPropagation()}
                              >
                                <ActionIcon
                                  variant="subtle"
                                  aria-label={
                                    getTranslation("TOOLTIP_ADD_ITEM") ||
                                    "Add item"
                                  }
                                  disabled={!canManage}
                                  onClick={() => openCreateItem(group)}
                                >
                                  <IconPlus size={16} />
                                </ActionIcon>
                                <ActionIcon
                                  variant="subtle"
                                  aria-label={
                                    getTranslation("TOOLTIP_EDIT_GROUP") ||
                                    "Edit group"
                                  }
                                  disabled={!canManage}
                                  onClick={() => openEditGroup(group)}
                                >
                                  <IconEdit size={16} />
                                </ActionIcon>
                                <ActionIcon
                                  variant="subtle"
                                  color="red"
                                  aria-label={
                                    getTranslation("TOOLTIP_DELETE_GROUP") ||
                                    "Delete group"
                                  }
                                  disabled={!canManage || group.isInUse}
                                  onClick={() => confirmDeleteGroup(group)}
                                >
                                  <IconTrash size={16} />
                                </ActionIcon>
                                <ActionIcon
                                  variant="subtle"
                                  aria-label={
                                    getTranslation("TOOLTIP_MOVE_GROUP_UP") ||
                                    "Move group up"
                                  }
                                  disabled={
                                    groupIndex === 0 ||
                                    !canManage ||
                                    movingKey !== null
                                  }
                                  loading={movingKey === `group-${group.id}--1`}
                                  onClick={() => void moveGroup(group.id, -1)}
                                >
                                  <IconArrowUp size={16} />
                                </ActionIcon>
                                <ActionIcon
                                  variant="subtle"
                                  aria-label={
                                    getTranslation("TOOLTIP_MOVE_GROUP_DOWN") ||
                                    "Move group down"
                                  }
                                  disabled={
                                    groupIndex === data.groups.length - 1 ||
                                    !canManage ||
                                    movingKey !== null
                                  }
                                  loading={movingKey === `group-${group.id}-1`}
                                  onClick={() => void moveGroup(group.id, 1)}
                                >
                                  <IconArrowDown size={16} />
                                </ActionIcon>
                              </Group>
                            </Table.Td>
                          </Table.Tr>
                          {!isCollapsed &&
                            group.items.map((item, itemIndex) => (
                              <Table.Tr key={item.id}>
                                {!data.hideWorkItemNumber && (
                                  <Table.Td title={`${item.id}`}>
                                    {displayNumber(
                                      item.itemNumber,
                                      item.itemSubnumber,
                                      item.itemCode,
                                    )}
                                  </Table.Td>
                                )}
                                <Table.Td>
                                  <Text
                                    size="sm"
                                    lineClamp={2}
                                    title={item.name}
                                  >
                                    {item.name}
                                  </Text>
                                </Table.Td>
                                <Table.Td>
                                  <Text size="sm" title={item.unitName}>
                                    {item.unitName}
                                  </Text>
                                </Table.Td>
                                <Table.Td
                                  ta="right"
                                  bg="white"
                                  style={actionColumnStyle}
                                >
                                  <Group justify="flex-end" gap={4}>
                                    <ActionIcon
                                      variant="subtle"
                                      aria-label={
                                        getTranslation("TOOLTIP_EDIT_ITEM") ||
                                        "Edit item"
                                      }
                                      disabled={!canManage}
                                      onClick={() => openEditItem(group, item)}
                                    >
                                      <IconEdit size={16} />
                                    </ActionIcon>
                                    <ActionIcon
                                      variant="subtle"
                                      color="red"
                                      aria-label={
                                        getTranslation("TOOLTIP_DELETE_ITEM") ||
                                        "Delete item"
                                      }
                                      disabled={!canManage || item.isInUse}
                                      onClick={() => confirmDeleteItem(item)}
                                    >
                                      <IconTrash size={16} />
                                    </ActionIcon>
                                    <ActionIcon
                                      variant="subtle"
                                      aria-label={
                                        getTranslation("TOOLTIP_MOVE_ITEM_UP") ||
                                        "Move item up"
                                      }
                                      disabled={
                                        itemIndex === 0 ||
                                        !canManage ||
                                        movingKey !== null
                                      }
                                      loading={
                                        movingKey === `item-${item.id}--1`
                                      }
                                      onClick={() =>
                                        void moveItem(group.id, item.id, -1)
                                      }
                                    >
                                      <IconArrowUp size={16} />
                                    </ActionIcon>
                                    <ActionIcon
                                      variant="subtle"
                                      aria-label={
                                        getTranslation(
                                          "TOOLTIP_MOVE_ITEM_DOWN",
                                        ) || "Move item down"
                                      }
                                      disabled={
                                        itemIndex === group.items.length - 1 ||
                                        !canManage ||
                                        movingKey !== null
                                      }
                                      loading={
                                        movingKey === `item-${item.id}-1`
                                      }
                                      onClick={() =>
                                        void moveItem(group.id, item.id, 1)
                                      }
                                    >
                                      <IconArrowDown size={16} />
                                    </ActionIcon>
                                  </Group>
                                </Table.Td>
                              </Table.Tr>
                            ))}
                        </Fragment>
                      );
                    })
                  )}
                </Table.Tbody>
              </Table>
            </Paper>
          </>
        )}
      </Stack>
      <Modal
        opened={groupModalOpened}
        onClose={() => setGroupModalOpened(false)}
        title={
          editingGroupId == null
            ? getTranslation("TITLE_ADD_GROUP") || "Add group"
            : getTranslation("TITLE_EDIT_GROUP") || "Edit group"
        }
        size="lg"
        centered
      >
        <Stack gap="sm">
          <TextInput
            label={getTranslation("LABEL_NAME") || "Name"}
            required
            value={groupForm.name}
            onChange={(event) =>
              setGroupForm((current) => ({
                ...current,
                name: event.currentTarget.value,
              }))
            }
          />
          <Select
            label={getTranslation("LABEL_ORDER") || "Order"}
            data={(metadata?.groupOrderOptions ?? []).map((option) => ({
              value: String(option.value),
              label: option.label,
            }))}
            value={String(groupForm.orderInGroup)}
            onChange={(value) =>
              setGroupForm((current) => ({
                ...current,
                orderInGroup: Number(value ?? 0),
              }))
            }
          />
          <Textarea
            label={getTranslation("LABEL_DESCRIPTION") || "Description"}
            minRows={3}
            value={groupForm.description ?? ""}
            onChange={(event) =>
              setGroupForm((current) => ({
                ...current,
                description: event.currentTarget.value,
              }))
            }
          />
          {data?.priceGroupsByObject && (
            <Select
              label={getTranslation("LABEL_OBJECT") || "Object"}
              clearable
              searchable
              disabled={
                metadataLoading && (metadata?.objectOptions.length ?? 0) === 0
              }
              placeholder={
                metadataLoading
                  ? getTranslation("PLACEHOLDER_LOADING_OBJECTS") ||
                    "Loading objects..."
                  : undefined
              }
              data={(metadata?.objectOptions ?? []).map((option) => ({
                value: String(option.id),
                label: option.name,
              }))}
              value={groupForm.objectId ? String(groupForm.objectId) : null}
              onChange={(value) =>
                setGroupForm((current) => ({
                  ...current,
                  objectId: value ? Number(value) : null,
                }))
              }
            />
          )}
          <Checkbox
            label={getTranslation("LABEL_INCLUDE_IN_REPORT") || "Include in report"}
            checked={groupForm.includeInReport}
            onChange={(event) =>
              setGroupForm((current) => ({
                ...current,
                includeInReport: event.currentTarget.checked,
              }))
            }
          />
          <Checkbox
            label={getTranslation("LABEL_ACTIVE") || "Active"}
            checked={groupForm.isActive}
            onChange={(event) =>
              setGroupForm((current) => ({
                ...current,
                isActive: event.currentTarget.checked,
              }))
            }
          />
          <Group justify="flex-end">
            <Button
              variant="default"
              onClick={() => setGroupModalOpened(false)}
            >
              {getTranslation("BUTTON_CANCEL") || "Cancel"}
            </Button>
            <Button loading={saving} onClick={() => void saveGroup()}>
              {getTranslation("BUTTON_SAVE") || "Save"}
            </Button>
          </Group>
        </Stack>
      </Modal>
      <Modal
        opened={itemModalOpened}
        onClose={() => setItemModalOpened(false)}
        title={
          editingItemId == null
            ? getTranslation("TITLE_ADD_ITEM") || "Add item"
            : getTranslation("TITLE_EDIT_ITEM") || "Edit item"
        }
        size="lg"
        centered
      >
        <Stack gap="sm">
          <Select
            label={getTranslation("LABEL_GROUP") || "Group"}
            required
            searchable
            data={(metadata?.groupOptions ?? []).map((option) => ({
              value: String(option.id),
              label: option.name,
            }))}
            value={itemForm.priceGroupId ? String(itemForm.priceGroupId) : null}
            onChange={(value) => void handleItemGroupChange(value)}
          />
          <TextInput
            label={getTranslation("LABEL_NAME") || "Name"}
            required
            value={itemForm.name}
            onChange={(event) =>
              setItemForm((current) => ({
                ...current,
                name: event.currentTarget.value,
              }))
            }
          />
          {!data?.hideWorkItemNumber && data?.useWorkItemCode && (
            <TextInput
              label={getTranslation("LABEL_CODE") || "Code"}
              required
              value={itemForm.itemCode ?? ""}
              onChange={(event) =>
                setItemForm((current) => ({
                  ...current,
                  itemCode: event.currentTarget.value,
                }))
              }
            />
          )}
          {!data?.hideWorkItemNumber && !data?.useWorkItemCode && (
            <Group grow>
              <NumberInput
                label={getTranslation("LABEL_MAIN_NUMBER") || "Main number"}
                required
                value={itemForm.itemNumber}
                min={0}
                onChange={(value) =>
                  setItemForm((current) => ({
                    ...current,
                    itemNumber: Number(value ?? 0),
                  }))
                }
              />
              <NumberInput
                label={getTranslation("LABEL_SUB_NUMBER") || "Sub number"}
                required
                value={itemForm.itemSubnumber}
                min={0}
                onChange={(value) =>
                  setItemForm((current) => ({
                    ...current,
                    itemSubnumber: Number(value ?? 0),
                  }))
                }
              />
            </Group>
          )}
          <Group grow>
            <Select
              label={getTranslation("LABEL_UNIT") || "Unit"}
              required
              searchable
              data={metadata?.unitOptions ?? []}
              value={itemForm.unitName || null}
              onChange={(value) =>
                setItemForm((current) => ({
                  ...current,
                  unitName: value ?? "",
                }))
              }
            />
            <Select
              label={getTranslation("LABEL_ORDER") || "Order"}
              data={(metadata?.groupOrderOptions ?? []).map((option) => ({
                value: String(option.value),
                label: option.label,
              }))}
              value={String(itemForm.orderInGroup)}
              onChange={(value) =>
                setItemForm((current) => ({
                  ...current,
                  orderInGroup: Number(value ?? 0),
                }))
              }
            />
          </Group>
          <Textarea
            label={getTranslation("LABEL_DESCRIPTION") || "Description"}
            minRows={3}
            value={itemForm.description ?? ""}
            onChange={(event) =>
              setItemForm((current) => ({
                ...current,
                description: event.currentTarget.value,
              }))
            }
          />
          <TextInput
            label={
              getTranslation("LABEL_INVOICING_REFERENCE_CODE") ||
              "Invoicing reference code"
            }
            value={itemForm.invoicingReferenceCode ?? ""}
            onChange={(event) =>
              setItemForm((current) => ({
                ...current,
                invoicingReferenceCode: event.currentTarget.value,
              }))
            }
          />
          <Textarea
            label={getTranslation("LABEL_REFERENCE_CODES") || "Reference codes"}
            minRows={3}
            value={itemForm.referenceCodes ?? ""}
            onChange={(event) =>
              setItemForm((current) => ({
                ...current,
                referenceCodes: event.currentTarget.value,
              }))
            }
          />
          <Group>
            <Checkbox
              label={getTranslation("LABEL_SHOW_TONS_M2") || "Show tons / m2"}
              checked={itemForm.requireAdditionalInfo}
              onChange={(event) =>
                setItemForm((current) => ({
                  ...current,
                  requireAdditionalInfo: event.currentTarget.checked,
                }))
              }
            />
            <Checkbox
              label={getTranslation("LABEL_INCLUDE_IN_REPORT") || "Include in report"}
              checked={itemForm.includeInReport}
              onChange={(event) =>
                setItemForm((current) => ({
                  ...current,
                  includeInReport: event.currentTarget.checked,
                }))
              }
            />
          </Group>
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setItemModalOpened(false)}>
              {getTranslation("BUTTON_CANCEL") || "Cancel"}
            </Button>
            <Button loading={saving} onClick={() => void saveItem()}>
              {getTranslation("BUTTON_SAVE") || "Save"}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Card>
  );
}
